import React, { useState, useEffect } from 'react';
import { FaSyncAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { handleUpdateStatus } from '../../shared/lib/adminApiHelpers';
import { trimData } from '../../shared/lib/formatters';
import Loader from '../../shared/components/layout/Loader';
import ModalShell from '../../shared/components/modal/ModalShell';
import ModalFormFooter from '../../shared/components/modal/ModalFormFooter';
import useBodyScrollLock from '../../shared/hooks/useBodyScrollLock';

const EditReportModal = ({ isOpen, onRequestClose, report, onEdit, fetchReport }) => {
    const [formData, setFormData] = useState({
        report: '',
        error: '',
        audio_language: '',
        report_language: '',
        status: '',
        comment: '',
        report_fetched: false,
        urgent: false,
        isPrivate: false,
        audio_duration: '',
        manual_retry: false,
        uses_custom_template: false,
        meta_type: '',
    });

    const [initialData, setInitialData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [regenerateLoading, setRegenerateLoading] = useState(false);

    useBodyScrollLock(isOpen);

    useEffect(() => {
        if (report) {
            const initialFormData = {
                report: report.report || '',
                error: report.error || '',
                audio_language: report.audio_language || '',
                report_language: report.report_language || '',
                status: report.status || '',
                comment: report.comment || '',
                report_fetched: report.report_fetched || false,
                urgent: report.urgent || false,
                isPrivate: report.private || false,
                audio_duration: report.audio_duration || '',
                manual_retry: report.manual_retry || false,
                uses_custom_template: report.uses_custom_template || false,
                meta_type: report.meta_type || '',
            };
            setFormData(initialFormData);
            setInitialData(initialFormData);
        }
    }, [report]);

    useEffect(() => {
        if (initialData) {
            const trimmedFormData = trimData(formData);
            const trimmedInitialData = trimData(initialData);

            const isFormDataChanged = JSON.stringify(trimmedFormData) !== JSON.stringify(trimmedInitialData);
            setIsSaveDisabled(!isFormDataChanged);
        }
    }, [formData, initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);
            await onEdit(report.id, formData);
        } catch (error) {
            console.error('Failed to edit report:', error);
            toast.error(error.message || 'Failed to edit report. Please try again.');
        } finally {
            onRequestClose();
            setIsLoading(false);
        }
    };

    const handleRegenerate = async () => {
        if (regenerateLoading) return; 
        setRegenerateLoading(true);
        await handleUpdateStatus({ documentId: report.id, status: 'regenerate' });
        await fetchReport(report.id);
        setRegenerateLoading(false);
        onRequestClose();
    }

    if (!isOpen) return null;

    const footer = (
        <ModalFormFooter
            onCancel={onRequestClose}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            isDisabled={isSaveDisabled}
        />
    );

    return (
        <ModalShell
            isOpen={isOpen}
            onClose={onRequestClose}
            title="Edit Report"
            footer={footer}
            maxWidthClass="md:max-w-6xl"
        >
            <form onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex flex-col mb-4 col-span-2">
                        <div className='flex justify-between items-center mb-2'>
                            <label className='text-gray-700 text-sm font-bold'>Report</label>
                            {report.report_fetched &&
                                <div 
                                    className='flex items-center gap-2 text-sm font-medium text-black/60 bg-green-200 px-2 py-1 rounded cursor-pointer hover:bg-green-300 border border-green-400'
                                    onClick={handleRegenerate}
                                >
                                    <p>Regenerate</p>
                                    {regenerateLoading ? 
                                        <Loader className={'text-green-500'} variant='small' /> :
                                        <FaSyncAlt className='text-green-500 hover:text-green-700 cursor-pointer'/>
                                    }
                                </div>
                            }
                        </div>
                        <textarea
                            name="report"
                            value={formData.report}
                            onChange={handleChange}
                            className="px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            rows="12"
                        />
                    </div>
                    {[
                        'error',
                        'audio_language',
                        'report_language',
                        'status',
                        'comment',
                        'meta_type',
                    ].map((field) => (
                        <div className="flex flex-col mb-4" key={field}>
                            <label className="block text-gray-700 text-sm font-bold mb-2">{field.replace(/_/g, ' ')}</label>
                            <input
                                type="text"
                                name={field}
                                value={formData[field]}
                                onChange={handleChange}
                                className="px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                    ))}
                    <div className="flex flex-col mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">Audio Duration</label>
                        <input
                            type="number"
                            step="0.01"
                            name="audio_duration"
                            value={formData.audio_duration}
                            onChange={handleChange}
                            className="px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <div className='flex flex-wrap gap-6 col-span-2 mb-4'>
                        <div className="flex items-center">
                            <label className="block text-gray-700 text-sm font-bold mr-2">Report Fetched</label>
                            <input
                                type="checkbox"
                                name="report_fetched"
                                checked={formData.report_fetched}
                                onChange={handleChange}
                                className="form-checkbox h-5 w-5 text-indigo-500"
                            />
                        </div>
                        <div className="flex items-center">
                            <label className="block text-gray-700 text-sm font-bold mr-2">Urgent</label>
                            <input
                                type="checkbox"
                                name="urgent"
                                checked={formData.urgent}
                                onChange={handleChange}
                                className="form-checkbox h-5 w-5 text-indigo-500"
                            />
                        </div>
                        <div className="flex items-center">
                            <label className="block text-gray-700 text-sm font-bold mr-2">Is Private</label>
                            <input
                                type="checkbox"
                                name="isPrivate"
                                checked={formData.isPrivate}
                                onChange={handleChange}
                                className="form-checkbox h-5 w-5 text-indigo-500"
                            />
                        </div>
                        <div className="flex items-center">
                            <label className="block text-gray-700 text-sm font-bold mr-2">Manual Retry</label>
                            <input
                                type="checkbox"
                                name="manual_retry"
                                checked={formData.manual_retry}
                                onChange={handleChange}
                                className="form-checkbox h-5 w-5 text-indigo-500"
                            />
                        </div>
                        <div className="flex items-center">
                            <label className="block text-gray-700 text-sm font-bold mr-2">Uses Custom Template</label>
                            <input
                                type="checkbox"
                                name="uses_custom_template"
                                checked={formData.uses_custom_template}
                                onChange={handleChange}
                                className="form-checkbox h-5 w-5 text-indigo-500"
                            />
                        </div>
                    </div>
                </div>
            </form>
        </ModalShell>
    );
};

export default EditReportModal;
